import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, FileText, MessageSquare, LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { cn } from "@/lib/utils";

const ITEMS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/blog", label: "Blog posts", icon: FileText },
  { to: "/admin/submissions", label: "Contact submissions", icon: MessageSquare },
];

export default function AdminLayout() {
  const { user, logout, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-zinc-500 font-mono text-xs">
        Verifying session…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location.pathname }} replace />;
  }

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-white/[0.06] p-6">
        <Link to="/" className="flex items-center gap-2.5 mb-10">
          <div className="h-8 w-8 rounded-full overflow-hidden border border-white/20 bg-white flex items-center justify-center shrink-0">
            <img src="/company.jpg" alt="Fullstack Endeavours" className="w-full h-full object-contain" />
          </div>
          <span className="font-display text-lg tracking-tight">Fullstack Endeavours</span>
        </Link>

        <nav className="space-y-1">
          {ITEMS.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`admin-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition",
                    isActive ? "bg-white/[0.06] text-foreground" : "text-zinc-400 hover:bg-white/[0.04] hover:text-foreground"
                  )
                }
              >
                <Icon className="h-4 w-4" /> {it.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="mt-auto space-y-3">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-xs text-zinc-500 hover:text-foreground hover:bg-white/[0.04] transition"
          >
            <ExternalLink className="h-3.5 w-3.5" /> View live site
          </Link>
          <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3">
            <div className="text-xs text-zinc-500">Signed in as</div>
            <div className="text-sm font-medium truncate">{user.email}</div>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-white/[0.04] border border-white/10 px-3 py-1.5 text-xs hover:bg-white/[0.08] transition"
            >
              <LogOut className="h-3 w-3" /> Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 glass-strong border-b border-white/[0.06] flex items-center justify-between px-4 py-3">
        <Link to="/admin" className="font-display text-base">Fullstack Endeavours Studio</Link>
        <button onClick={logout} className="text-xs text-zinc-400 inline-flex items-center gap-1">
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 md:pt-0 pt-16">
        <div className="max-w-5xl mx-auto p-6 md:p-10">
          {/* Mobile nav */}
          <div className="md:hidden flex gap-2 mb-6 overflow-x-auto">
            {ITEMS.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                className={({ isActive }) =>
                  cn(
                    "shrink-0 px-4 py-1.5 rounded-full text-xs border transition",
                    isActive ? "bg-nexus-cyan text-nexus-ink border-nexus-cyan" : "border-white/10 bg-white/[0.03] text-zinc-300"
                  )
                }
              >
                {it.label}
              </NavLink>
            ))}
          </div>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
